/**
 * Process-local event bus, replacing DeviceEventEmitter in shared code.
 *
 * Same emit/addListener surface the auth flow relied on, so listeners keep the
 * `{ remove() }` subscription shape.
 */
type Listener = (payload?: unknown) => void;

export interface EventSubscription {
  remove: () => void;
}

const listeners = new Map<string, Set<Listener>>();

export const eventBus = {
  addListener: <T = unknown>(
    event: string,
    listener: (payload: T) => void,
  ): EventSubscription => {
    const existing = listeners.get(event) ?? new Set<Listener>();
    existing.add(listener as Listener);
    listeners.set(event, existing);

    return {
      remove: () => {
        const current = listeners.get(event);
        if (!current) {
          return;
        }
        current.delete(listener as Listener);
        if (current.size === 0) {
          listeners.delete(event);
        }
      },
    };
  },

  emit: (event: string, payload?: unknown): void => {
    const current = listeners.get(event);
    if (!current) {
      return;
    }
    // Copy first: a listener may unsubscribe itself while being notified.
    for (const listener of Array.from(current)) {
      try {
        listener(payload);
      } catch (error) {
        console.warn(`[EventBus] listener for "${event}" threw:`, error);
      }
    }
  },
};
